/**
 * Unit Quantity Codes — the GST portal's closed list.
 *
 * GSTR-1's HSN summary and the e-invoice schema both take the unit from THIS
 * list and nothing else: the portal offers it as a dropdown with manual entry
 * switched off, so a line measured in "Trip" or "Lnt" has nowhere to go.
 *
 * That is why it is a second field rather than a replacement for the unit
 * master. A BOQ is written in the units the site works in — Cu.M, Trip, Lnt,
 * Daily — and docs/13 makes the BOQ template's `Masters` sheet a value
 * contract, so those names cannot be taken away. Each of them maps to the code
 * the return has to carry, and the ones that genuinely measure nothing the GST
 * list knows about map to `OTH`, which is what the portal provides it for.
 *
 * Codes are the statutory three letters; the label is the portal's own wording
 * so the two screens can be read side by side (GRAMMES and MILILITRE are
 * spelled the portal's way, not the dictionary's).
 */
export const UQC = [
  { code: 'BAG', label: 'BAGS' },
  { code: 'BAL', label: 'BALE' },
  { code: 'BDL', label: 'BUNDLES' },
  { code: 'BKL', label: 'BUCKLES' },
  { code: 'BOU', label: 'BILLION OF UNITS' },
  { code: 'BOX', label: 'BOX' },
  { code: 'BTL', label: 'BOTTLES' },
  { code: 'BUN', label: 'BUNCHES' },
  { code: 'CAN', label: 'CANS' },
  { code: 'CBM', label: 'CUBIC METERS' },
  { code: 'CCM', label: 'CUBIC CENTIMETERS' },
  { code: 'CMS', label: 'CENTIMETERS' },
  { code: 'CTN', label: 'CARTONS' },
  { code: 'DOZ', label: 'DOZENS' },
  { code: 'DRM', label: 'DRUMS' },
  { code: 'GGK', label: 'GREAT GROSS' },
  { code: 'GMS', label: 'GRAMMES' },
  { code: 'GRS', label: 'GROSS' },
  { code: 'GYD', label: 'GROSS YARDS' },
  { code: 'KGS', label: 'KILOGRAMS' },
  { code: 'KLR', label: 'KILOLITRE' },
  { code: 'KME', label: 'KILOMETRE' },
  { code: 'MLT', label: 'MILILITRE' },
  { code: 'MTR', label: 'METERS' },
  { code: 'MTS', label: 'METRIC TON' },
  { code: 'NOS', label: 'NUMBERS' },
  { code: 'PAC', label: 'PACKS' },
  { code: 'PCS', label: 'PIECES' },
  { code: 'PRS', label: 'PAIRS' },
  { code: 'QTL', label: 'QUINTAL' },
  { code: 'ROL', label: 'ROLLS' },
  { code: 'SET', label: 'SETS' },
  { code: 'SQF', label: 'SQUARE FEET' },
  { code: 'SQM', label: 'SQUARE METERS' },
  { code: 'SQY', label: 'SQUARE YARDS' },
  { code: 'TBS', label: 'TABLETS' },
  { code: 'TGM', label: 'TEN GROSS' },
  { code: 'THD', label: 'THOUSANDS' },
  { code: 'TON', label: 'TONNES' },
  { code: 'TUB', label: 'TUBES' },
  { code: 'UGS', label: 'US GALLONS' },
  { code: 'UNT', label: 'UNITS' },
  { code: 'YDS', label: 'YARDS' },
  { code: 'OTH', label: 'OTHERS' },
] as const

export type UqcCode = (typeof UQC)[number]['code']

export const UQC_CODES: readonly string[] = UQC.map((u) => u.code)

/** `BAG-BAGS`, the way the portal writes it in its own dropdown. */
export const uqcLabel = (code: string): string => {
  const found = UQC.find((u) => u.code === code)
  return found ? `${found.code}-${found.label}` : code
}

/**
 * What the 28 units of the BOQ template's `Masters` sheet (docs/18 §3) mean in
 * GST terms.
 *
 * Ten of them answer `OTH`, and that is the right answer rather than a gap:
 * a Trip, a Day, a Job and a Lump sum are how construction is priced and none
 * of them is a quantity of anything the GST list measures. Seeded as the
 * default for a tenant starting from the template; an administrator can change
 * any of them, and a unit they add themselves starts with none.
 */
export const UQC_FOR_TEMPLATE_UNIT: Readonly<Record<string, UqcCode>> = {
  Bag: 'BAG',
  'Cu.M': 'CBM',
  Daily: 'OTH',
  Days: 'OTH',
  Each: 'NOS',
  Job: 'OTH',
  KG: 'KGS',
  LS: 'OTH',
  /*
    There is no plain litre in the GST list — only KLR (kilolitre) and MLT
    (mililitre) — and the usual advice is to file litres as KLR.

    Not here, because we do not convert the quantity: a 500-litre order filed
    as KLR reads as 500 kilolitres, a thousandfold overstatement on the return.
    OTH carries the quantity honestly. Change it to KLR only alongside a
    conversion, and it is one row on this master to change.
  */
  Litres: 'OTH',
  Lnt: 'OTH',
  Lot: 'OTH',
  M2: 'SQM',
  MT: 'MTS',
  MTR: 'MTR',
  Months: 'OTH',
  NOS: 'NOS',
  Pair: 'PRS',
  Pc: 'PCS',
  Point: 'OTH',
  Roll: 'ROL',
  SQM: 'SQM',
  Set: 'SET',
  'Sq Ft': 'SQF',
  // TONNES, not METRIC TON: the template carries both, and MT is the metric
  // tonne above.
  TONS: 'TON',
  Trip: 'OTH',
  Units: 'UNT',
  box: 'BOX',
  pkt: 'PAC',
}
